import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import Modal from "components/Modal/Modal"
import { Guid } from "@/types"
import { IGetFreightUnit } from "models/Asset"
import toastMessages from "stores/toastMessages"
import AssetService from "services/AssetService"
import { getTranslation } from "utils/translations"
import { useTenantSettings } from "stores/tenantSettings"
import Text from "components/Text/Text"
import Features from "components/Features/Features"
import NoValue from "components/NoValue/NoValue"
import { Grid, GridItem } from "components/Grid"
import FreightUnits from "components/FreightUnits/FreightUnits"

interface IAssetFeaturesModalProps {
    assetId: Guid
    onClose: () => void
}

const AssetFeaturesModal: FC<IAssetFeaturesModalProps> = ({
    assetId,
    onClose,
}) => {
    const { t } = useTranslation()
    const { tenantPrimaryLanguageIsoCode } = useTenantSettings()
    const [features, setFeatures] = useState<string | null>(null)
    const [freightUnits, setFreightUnits] = useState<IGetFreightUnit[] | null>(null)
    const loading = features === null || freightUnits === null

    const fetchFeaturesAndFreightUnits = useCallback(async () => {
        try {
            const asset = await AssetService.getAsset(assetId)
            if (!asset) throw new Error()
            setFeatures(asset.features ? getTranslation(asset.features, tenantPrimaryLanguageIsoCode) : "")
            setFreightUnits(asset.freightUnits)
        } catch(e) {
            toastMessages.showSystemError()
            onClose()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetId, tenantPrimaryLanguageIsoCode])

    useEffect(() => {
        fetchFeaturesAndFreightUnits()
    }, [fetchFeaturesAndFreightUnits])

    return (
        <Modal
            title={t('features.features')}
            maxWidth="586px"
            onClose={onClose}
            isBusy={loading}>
            <Grid gap="3.2rem">
                {features !== null &&
                    <GridItem>
                        <Text type="h4" className="mb-3">{t('features.features')}</Text>
                        {features
                            ? <Features features={features} />
                            : <NoValue>{t('features.noFeaturesAdded')}</NoValue>
                        }
                    </GridItem>
                }
                {!!freightUnits &&
                    <GridItem>
                        {freightUnits.length > 0
                            ? <FreightUnits freightUnits={freightUnits} />
                            : <>
                                <Text type="h4" className="mb-3">{t('freightUnit.freightUnits')}</Text>
                                <NoValue>{t('freightUnit.noFreightUnitsAdded')}</NoValue>
                            </>
                        }
                    </GridItem>
                }
            </Grid>
        </Modal>
    )
}

export default AssetFeaturesModal
